import PortalShell from "../../components/PortalShell";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CalendarDays, Clock, CheckCircle2 } from "lucide-react";

const SLOTS = ["16:00-17:00","17:00-18:00","18:00-19:00","19:00-20:00"];
const LESSON_TYPES = ["1-to-1 Tutoring","Small Group (3-5)","Mock Review","Exam Technique"];

function nextDates(n=14){
  const arr=[]; const d = new Date();
  for(let i=1;i<=n;i++){ const t=new Date(d); t.setDate(d.getDate()+i); arr.push(t); }
  return arr;
}

export default function BookLesson() {
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [type, setType] = useState(LESSON_TYPES[0]);
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/bookings/me").then(r => setMine(r.data.bookings || [])).catch(()=>{});
  useEffect(() => { load(); }, []);

  const book = async e => {
    e.preventDefault();
    if (!date || !slot) return toast.error("Pick a date and time slot");
    setLoading(true);
    try {
      await api.post("/bookings", { date, time_slot: slot, lesson_type: type, topic, notes });
      toast.success("Lesson booked. See you soon!");
      setDate(""); setSlot(""); setTopic(""); setNotes(""); load();
    } catch { toast.error("Couldn't book"); } finally { setLoading(false); }
  };

  return (
    <PortalShell role="student" title="Book a Lesson" subtitle="Reserve time with your tutor. 1:1 or small group.">
      <div className="grid md:grid-cols-[1fr_340px] gap-6">
        <form onSubmit={book} className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-3 block">Pick a date</label>
            <div className="grid grid-cols-7 gap-2">
              {nextDates().map(d => {
                const iso = d.toISOString().slice(0,10);
                const active = date === iso;
                return (
                  <button type="button" key={iso} onClick={()=>setDate(iso)} data-testid={`date-${iso}`}
                    className={`p-2 rounded-md border text-xs text-center transition ${active?'bg-brand-navy text-white border-brand-navy':'bg-white hover:bg-slate-50 border-slate-200'}`}>
                    <div className="text-[10px] uppercase tracking-widest opacity-70">{d.toLocaleDateString('en-GB',{weekday:'short'})}</div>
                    <div className="font-serif text-lg">{d.getDate()}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-3 block">Time slot</label>
            <div className="grid grid-cols-4 gap-2">
              {SLOTS.map(s => (
                <button type="button" key={s} onClick={()=>setSlot(s)} data-testid={`slot-${s}`}
                  className={`p-2.5 rounded-md border text-sm transition ${slot===s?'bg-brand-gold border-brand-gold brand-navy font-semibold':'bg-white hover:bg-slate-50 border-slate-200'}`}>{s}</button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Lesson type</label>
              <select value={type} onChange={e=>setType(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm bg-white" data-testid="booking-type">
                {LESSON_TYPES.map(l=><option key={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Topic (optional)</label>
              <input value={topic} onChange={e=>setTopic(e.target.value)} placeholder="e.g. Quadratics" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm" data-testid="booking-topic"/>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Notes (optional)</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm" data-testid="booking-notes"/>
          </div>

          <button disabled={loading} className="btn-gold px-6 py-3 rounded-md" data-testid="booking-submit">{loading?"Booking…":"Confirm booking"}</button>
        </form>

        <aside className="bg-slate-50 rounded-xl border border-slate-200 p-6">
          <h4 className="font-serif text-lg brand-navy mb-4">Your lessons</h4>
          {mine.length === 0 && <p className="text-sm text-slate-500">No bookings yet.</p>}
          <div className="space-y-3">
            {mine.map(b => (
              <div key={b.id} className="bg-white rounded-md border border-slate-200 p-4" data-testid={`booking-${b.id}`}>
                <div className="flex items-center gap-2 text-xs gold-text mb-2"><CalendarDays size={12}/>{b.date}</div>
                <div className="font-serif brand-navy">{b.time_slot}</div>
                <div className="text-xs text-slate-600 mt-1">{b.lesson_type}</div>
                {b.topic && <div className="text-xs text-slate-400 mt-1">{b.topic}</div>}
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-green-700"><CheckCircle2 size={11}/> {b.status}</div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </PortalShell>
  );
}
